import React, { useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../../lib/http";
import { postLoginHref } from "../../lib/djangoAppUrl";

export function SignupPage() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNeedsVerification(false);

    const resp = await apiFetch("/api/auth/signup/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        first_name: firstName,
        last_name: lastName,
        email,
        password: p1,
        password_confirm: p2
      })
    });

    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      const msg =
        data?.error ||
        (data?.errors
          ? Object.values(data.errors)
              .flat()
              .slice(0, 2)
              .join(" ")
          : "Signup failed.");
      setError(msg);
      setBusy(false);
      return;
    }

    if (data?.redirect) {
      const target = String(data.redirect);
      // In dev the SPA runs on a different origin; ensure we navigate to Django for post-login.
      if (target === "/accounts/post-login/" || target.startsWith("/accounts/post-login/?")) {
        window.location.href = postLoginHref();
      } else {
        window.location.href = target;
      }
      setBusy(false);
      return;
    }
    if (data?.requires_verification) {
      setNeedsVerification(true);
      setBusy(false);
      return;
    }

    setBusy(false);
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 text-[15px] selection:bg-cyan-400/30 selection:text-cyan-50">
      <div className="mx-auto max-w-md min-h-screen flex flex-col">
        <main className="flex flex-1 items-center justify-center px-4 py-6">
          <section className="w-full rounded-3xl bg-slate-900/40 ring-1 ring-cyan-300/25 p-5">
            <header className="flex items-center justify-center gap-3 text-center">
              <img src="/static/img/site-logo.png" alt="" className="h-16 w-16 object-contain" />
              <div className="min-w-0">
                <div
                  className="text-2xl font-semibold tracking-tight"
                  style={{
                    textShadow:
                      "0 0 14px rgba(6,182,212,0.48), 0 0 22px rgba(217,70,239,0.34)",
                    filter: "drop-shadow(0 0 8px rgba(255,255,255,0.28))"
                  }}
                >
                  <span className="text-cyan-200">Human</span>{" "}
                  <span className="text-fuchsia-200">Jukebox</span>
                </div>
              </div>
            </header>

            {needsVerification ? (
              <div className="mt-4 rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-300/25 px-4 py-3 text-sm text-emerald-50">
                Account created. Check your email for a verification link.
              </div>
            ) : null}
            {error ? (
              <div className="mt-4 rounded-2xl bg-rose-500/15 ring-1 ring-rose-300/25 px-4 py-3 text-sm text-rose-50">
                {error}
              </div>
            ) : null}

            <form onSubmit={onSubmit} className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <div className="text-sm text-slate-200">First name</div>
                  <input
                    className="mt-1 w-full rounded-2xl bg-slate-950/40 ring-1 ring-white/10 px-4 py-3 text-base text-slate-50 outline-none focus:ring-2 focus:ring-cyan-300/40"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </label>
                <label className="block">
                  <div className="text-sm text-slate-200">Last name</div>
                  <input
                    className="mt-1 w-full rounded-2xl bg-slate-950/40 ring-1 ring-white/10 px-4 py-3 text-base text-slate-50 outline-none focus:ring-2 focus:ring-cyan-300/40"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </label>
              </div>

              <label className="block">
                <div className="text-sm text-slate-200">Email</div>
                <div className="mt-1 relative">
                  <input
                    className="w-full rounded-2xl bg-slate-950/40 ring-1 ring-white/10 px-4 py-3 pr-12 text-base text-slate-50 outline-none focus:ring-2 focus:ring-cyan-300/40"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                  />
                  <i className="fa-regular fa-envelope absolute right-4 top-1/2 -translate-y-1/2 text-slate-300" />
                </div>
              </label>

              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <div className="text-sm text-slate-200">Password</div>
                  <div className="mt-1 relative">
                    <input
                      className="w-full rounded-2xl bg-slate-950/40 ring-1 ring-white/10 px-4 py-3 pr-12 text-base text-slate-50 outline-none focus:ring-2 focus:ring-fuchsia-300/40"
                      type="password"
                      value={p1}
                      onChange={(e) => setP1(e.target.value)}
                      required
                      placeholder="••••••••"
                    />
                    <i className="fa-solid fa-lock absolute right-4 top-1/2 -translate-y-1/2 text-slate-300" />
                  </div>
                </label>
                <label className="block">
                  <div className="text-sm text-slate-200">Confirm</div>
                  <div className="mt-1 relative">
                    <input
                      className="w-full rounded-2xl bg-slate-950/40 ring-1 ring-white/10 px-4 py-3 pr-12 text-base text-slate-50 outline-none focus:ring-2 focus:ring-fuchsia-300/40"
                      type="password"
                      value={p2}
                      onChange={(e) => setP2(e.target.value)}
                      required
                      placeholder="••••••••"
                    />
                    <i className="fa-solid fa-shield-halved absolute right-4 top-1/2 -translate-y-1/2 text-slate-300" />
                  </div>
                </label>
              </div>

              <div className="mt-2 flex flex-col gap-1.5">
                <button
                  className="w-full rounded-2xl bg-cyan-500/20 hover:bg-cyan-500/25 ring-1 ring-cyan-300/30 px-4 py-3 text-sm font-semibold text-cyan-50 disabled:opacity-60"
                  type="submit"
                  disabled={busy}
                >
                  {busy ? "…" : "Create account"}
                </button>

                <p className="border-t border-white/10 pt-2 text-center text-sm text-slate-300">
                  By signing up, you agree to our{" "}
                  <a
                    className="text-cyan-100 underline decoration-cyan-300/30 underline-offset-4"
                    href="/terms/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms
                  </a>{" "}
                  and{" "}
                  <a
                    className="text-cyan-100 underline decoration-cyan-300/30 underline-offset-4"
                    href="/privacy/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>

              <div className="pt-2 text-center text-sm text-slate-300">
                Already have an account?{" "}
                <Link className="text-fuchsia-100 underline decoration-fuchsia-300/30 underline-offset-4" to="/login">
                  Sign in
                </Link>
              </div>
            </form>
          </section>
        </main>
      </div>
    </div>
  );
}

